import { Button, Stack, Tooltip, Typography } from "@mui/material";
import type {
  DataGridPremiumProps,
  GridColDef,
  GridGroupingColDefOverride,
  GridRenderCellParams,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { useGridApiContext, useGridApiRef } from "@mui/x-data-grid-premium";
import { useMemo, useRef } from "react";
import InlineItemsList from "@entrilia/shared/components/InlineItemsList.tsx";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis.tsx";
import DataGrid from "@entrilia/shared/components/grid/DataGrid.tsx";
import DataGridGroupHeader from "@entrilia/shared/components/grid/DataGridGroupHeader.tsx";
import { useGridGroupsExpanded } from "@entrilia/shared/components/grid/gridHooks.ts";
import { convertISODate } from "@entrilia/shared/utilities/dateUtils.ts";
import { nameof } from "@entrilia/shared/utilities/typeHelper.ts";
import type { ReportTemplate, ReportTemplateDto } from "../../../../../api/types/svTypes.ts";
import { ExpandCollapseButton } from "../reports/components/ExpandCollapseButton.tsx";
import NewLabel from "../../../../common/NewLabel.tsx";
import { formatFundType } from "./helpers/reportTemplateHelper.ts";

interface Props {
  templates: ReportTemplateDto[];
  isReportCreating?: boolean;
  onTemplateNameClick: (template: ReportTemplate) => void;
  onQuickUse: (template: ReportTemplate) => void;
}

const getRowId = (row: ReportTemplateDto) => row.reportId;

export default function CompanyTemplatesGrid({ templates, isReportCreating, onTemplateNameClick, onQuickUse }: Props) {
  const apiRef = useGridApiRef();
  const sortModelRef = useRef<GridSortModel>([]);

  const { isGroupExpanded } = useGridGroupsExpanded(apiRef);

  const groupColumnDef = useMemo(
    (): GridGroupingColDefOverride => ({
      hideDescendantCount: true,
      headerName: "Name",
      sortable: true,
      aggregable: false,
      hideable: false,
      pinnable: false,
      filterable: false,
      flex: 1,
      sortComparator: (_v1, _v2, row1, row2) => {
        const direction = sortModelRef.current?.[0]?.sort ?? "asc";
        if (row1.rowNode.type === "group" && row2.rowNode.type === "group") {
          const firstGroupOrder = templates.find((r) => r.groupName === row1.value)?.groupOrder || 0;
          const secondGroupOrder = templates.find((r) => r.groupName === row2.value)?.groupOrder || 0;
          return direction === "asc" ? firstGroupOrder - secondGroupOrder : secondGroupOrder - firstGroupOrder;
        }
        const firstTemplateName = templates.find((r) => r.reportId === row1.rowNode.id)?.name || "";
        const secondTemplateName = templates.find((r) => r.reportId === row2.rowNode.id)?.name || "";
        return firstTemplateName.localeCompare(secondTemplateName);
      },
      renderHeader: (params) => <DataGridGroupHeader params={params} />,
      renderCell: (params: GridRenderCellParams<ReportTemplate, unknown, unknown>) => {
        if (params.rowNode.type === "group") {
          return <GroupName params={params} />;
        }
        return <NameColumn template={params.row} onClick={onTemplateNameClick} />;
      },
    }),
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [templates]
  );

  const columns = useMemo(
    (): GridColDef<ReportTemplateDto>[] => [
      { field: nameof<ReportTemplateDto>("reportType"), headerName: "Report Type", minWidth: 100 },
      {
        field: nameof<ReportTemplateDto>("fundTypes"),
        headerName: "Fund Type",
        sortable: false,
        minWidth: 140,
        renderCell: (params) => {
          if (params.rowNode.type === "group") {
            return null;
          }
          return !params.row?.fundTypes || params.row?.fundTypes?.length === 0 ? (
            "-"
          ) : (
            <InlineItemsList<string>
              displayCount={1}
              items={params.row.fundTypes.map((f) => formatFundType(f))}
              propGetter={(prop) => prop}
            />
          );
        },
      },
      {
        field: nameof<ReportTemplateDto>("createdAt"),
        headerName: "Added At",
        valueFormatter: (value) => convertISODate(value),
        renderCell: ({ formattedValue }) => (
          <Typography className="MuiDataGrid-cellContent" color={"secondary"}>
            {formattedValue}
          </Typography>
        ),
      },
      {
        field: "actions",
        sortable: false,
        resizable: false,
        headerName: "Actions",
        cellClassName: "grid-row-actions",
        align: "center",
        headerAlign: "center",
        minWidth: 105,
        renderCell: ({ row, rowNode }) => {
          if (rowNode.type === "group") {
            return null;
          }

          return (
            <Stack>
              <Tooltip title={"Use this template"} placement="bottom" arrow disableInteractive>
                <Button variant="text" color="primary" disabled={isReportCreating} onClick={() => onQuickUse(row)}>
                  Quick Use
                </Button>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [isReportCreating, onQuickUse]
  );

  return (
    <DataGrid<ReportTemplateDto>
      apiRef={apiRef}
      treeData
      getTreeDataPath={getTreeDataPath}
      columns={columns}
      rows={templates}
      getRowId={getRowId}
      hideFooter
      isGroupExpandedByDefault={isGroupExpanded}
      loading={false}
      groupingColDef={groupColumnDef}
      disableRowSelectionOnClick
      disableColumnMenu
      disableColumnReorder
      sortingOrder={["asc", "desc"]}
      onSortModelChange={(model) => (sortModelRef.current = model)}
    />
  );
}

const getTreeDataPath: DataGridPremiumProps["getTreeDataPath"] = (row: ReportTemplateDto) => [
  row.groupName,
  row.reportId,
];

function GroupName({ params }: { params: GridRenderCellParams }) {
  const apiRef = useGridApiContext();

  if (params.rowNode.type !== "group") {
    return null;
  }

  const expanded = params.rowNode.childrenExpanded === true;

  return (
    <>
      <ExpandCollapseButton
        expanded={expanded}
        onClick={(value) => {
          apiRef.current?.setRowChildrenExpansion(params.id, value);
        }}
      />
      <Typography variant="subtitle2" sx={{ pl: 1 }}>
        {params.value} ({params.rowNode.children.length})
      </Typography>
    </>
  );
}

function NameColumn({ template, onClick }: { template: ReportTemplate; onClick: (template: ReportTemplate) => void }) {
  return (
    <Typography
      component={"div"}
      noWrap
      className="highlighted-action"
      onClick={() => onClick(template)}
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "center",
        color: "text.primary",
        pl: 5.3,
        cursor: "pointer",
        "&:hover": { textDecoration: "underline" },
      }}
    >
      <TypographyTooltipEllipsis text={template.name} />

      {template.isNew && <NewLabel />}
    </Typography>
  );
}
